import type { MailIdentityKind } from '@embertime/shared'
import { Info } from 'lucide-react'
import { useId, useState } from 'react'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { IdentityKindIcon } from './identity-list'

const CHOICES: ReadonlyArray<{
  kind: MailIdentityKind
  title: string
  text: string
  example: string
}> = [
  {
    kind: 'own_domain',
    title: 'Eigene Domain',
    text: 'Adresse auf Deinem eigenen Mailserver. Ember zeigt Dir, welche DNS-Einträge noch fehlen.',
    example: 'z. B. info@agentur-beispiel.de',
  },
  {
    kind: 'external_smtp',
    title: 'Fremder Postausgang (SMTP)',
    text: 'Adresse einer fremden Domain. Ember sendet über deren SMTP-Server mit Deinen Zugangsdaten.',
    example: 'z. B. m.muster@partner-beispiel.de',
  },
]

/** Sheet „Absender hinzufügen“ (#839, Mockup „Typwahl“): how the mail leaves. */
export function IdentityAddSheet({
  open,
  accountLabel,
  initialKind = 'own_domain',
  onClose,
  onNext,
}: {
  open: boolean
  accountLabel: string
  initialKind?: MailIdentityKind
  onClose: () => void
  onNext: (kind: MailIdentityKind) => void
}) {
  const [kind, setKind] = useState<MailIdentityKind>(initialKind)
  const name = useId()
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Absender hinzufügen"
      description={`Für Konto „${accountLabel}“`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => onNext(kind)}>
            Weiter
          </Button>
        </>
      }
    >
      <fieldset className="space-y-3 pb-2">
        <legend className="mb-3 text-sm text-text-secondary">Wie wird die Mail verschickt?</legend>
        {CHOICES.map((choice) => {
          const checked = kind === choice.kind
          return (
            <label
              key={choice.kind}
              className={cn(
                'flex cursor-pointer gap-3 rounded-2xl border p-4 transition-colors',
                checked
                  ? 'border-accent/60 bg-accent/5'
                  : 'border-border-default hover:bg-bg-overlay',
              )}
            >
              <input
                type="radio"
                name={name}
                value={choice.kind}
                checked={checked}
                onChange={() => setKind(choice.kind)}
                className="mt-1.5 h-5 w-5 shrink-0 accent-[rgb(var(--c-accent))]"
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <IdentityKindIcon kind={choice.kind} size="sm" />
                  <span className="font-semibold text-text-primary">{choice.title}</span>
                </span>
                <span className="mt-1.5 block text-sm text-text-secondary">{choice.text}</span>
                <span className="mt-1.5 block break-all font-mono text-xs text-text-tertiary">
                  {choice.example}
                </span>
              </span>
            </label>
          )
        })}
        <p className="flex gap-2 pt-1 text-xs text-text-tertiary">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Beliebig viele Absender je Konto. Später jederzeit änderbar oder löschbar.
        </p>
      </fieldset>
    </Dialog>
  )
}
